import { useState } from "react";
import { Box, FormControl, FormControlLabel, FormLabel, Popover, Radio, RadioGroup, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ButtonOne from "../../common/normal-button";
import typography from "../../cssUtils/typography";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";
import { MEMBERS_EXPORT_ENDPOINT } from "../../config/urls";
import { buildQuery, messageFrom } from "./membersApi";
import { filtersToParams } from "./membersUtils";

const MAX_SELECTED = 1000;

/**
 * Export dropdown (spec 3): scope (all / current filter / selected rows) and format
 * (CSV or Excel), with the row count shown before confirming.
 */
const ExportMenu = ({ api, notify, filters, filteredCount, totalCount, selectedIds }) => {
  const [anchor, setAnchor] = useState(null);
  const [scope, setScope] = useState("filtered");
  const [format, setFormat] = useState("csv");
  const [busy, setBusy] = useState(false);

  const tooManySelected = selectedIds.length > MAX_SELECTED;
  const counts = { all: totalCount, filtered: filteredCount, selected: selectedIds.length };
  const effectiveScope = scope === "selected" && !selectedIds.length ? "filtered" : scope;
  const count = counts[effectiveScope];
  const unavailable = effectiveScope === "selected" && tooManySelected;

  const open = (event) => {
    // Opening with rows ticked defaults to exporting them.
    setScope(selectedIds.length ? "selected" : "filtered");
    setAnchor(event.currentTarget);
  };

  const run = async () => {
    const params = { format, scope: effectiveScope, ordering: filters.ordering };
    if (effectiveScope === "filtered") {
      const { page, page_size: pageSize, ...rest } = filtersToParams(filters); // eslint-disable-line no-unused-vars
      Object.assign(params, rest);
    }
    if (effectiveScope === "selected") params.ids = selectedIds.join(",");
    setBusy(true);
    try {
      await api.download(`${MEMBERS_EXPORT_ENDPOINT}${buildQuery(params)}`, `members.${format}`);
      notify(`Exported ${count} ${count === 1 ? "member" : "members"} as ${format === "csv" ? "CSV" : "Excel"}`);
      setAnchor(null);
    } catch (error) {
      notify(messageFrom(error, "The export failed. Please try again."), { severity: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <ButtonOne variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={open} aria-haspopup="dialog">
        Export
      </ButtonOne>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => !busy && setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2.5, width: 340, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl>
            <FormLabel sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
              What to export
            </FormLabel>
            <RadioGroup value={effectiveScope} onChange={(event) => setScope(event.target.value)}>
              <FormControlLabel value="all" control={<Radio size="small" />} label={`All members (${counts.all ?? "…"})`} />
              <FormControlLabel
                value="filtered"
                control={<Radio size="small" />}
                label={`Current filter, search and tab (${counts.filtered ?? "…"})`}
              />
              <FormControlLabel
                value="selected"
                disabled={!selectedIds.length}
                control={<Radio size="small" />}
                label={`Selected rows (${counts.selected})`}
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
              Format
            </FormLabel>
            <RadioGroup row value={format} onChange={(event) => setFormat(event.target.value)}>
              <FormControlLabel value="csv" control={<Radio size="small" />} label="CSV" />
              <FormControlLabel value="xlsx" control={<Radio size="small" />} label="Excel (.xlsx)" />
            </RadioGroup>
          </FormControl>
          {unavailable && (
            <Typography role="alert" sx={{ ...typography.bodySmall, color: ColorsConstants.error }}>
              Select at most {MAX_SELECTED} members to export, or export the current filter instead.
            </Typography>
          )}
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            Same columns as the upload template, so an exported file can be edited and uploaded again.
          </Typography>
          <ButtonOne onClick={run} disabled={busy || unavailable || count === 0}>
            {busy ? "Exporting…" : `Export ${count ?? ""} ${count === 1 ? "member" : "members"}`.replace("  ", " ")}
          </ButtonOne>
        </Box>
      </Popover>
    </>
  );
};

export default ExportMenu;
